import { useState, useEffect } from 'react';
import { orchestratorApi } from '../api/orchestrator';

interface Model {
  id: string;
  name: string;
  description?: string;
  pricing?: {
    prompt?: string;
    completion?: string;
  };
  context_length?: number;
}

interface ComponentPreference {
  name: string;
  displayName: string;
  description: string;
  primary: string;
  backup: string;
}

interface ModelStats {
  promptCost: number;
  completionCost: number;
  contextLength: number;
}

export default function ModelPreferences() {
  const [components, setComponents] = useState<ComponentPreference[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProvider, setFilterProvider] = useState<string>('all');
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();

      // Load models and preferences in parallel
      const [modelsRes, prefsRes] = await Promise.all([
        fetch('/api/admin/openrouter/models', { headers: authHeaders }),
        fetch('/api/admin/model-preferences', { headers: authHeaders })
      ]);

      if (!modelsRes.ok) {
        throw new Error(`Failed to load models: ${modelsRes.status} ${modelsRes.statusText}`);
      }

      if (!prefsRes.ok) {
        throw new Error(`Failed to load preferences: ${prefsRes.status} ${prefsRes.statusText}`);
      }

      const modelsData = await modelsRes.json();
      const prefsData = await prefsRes.json();

      setModels(modelsData.models || []);
      setComponents(prefsData.components || []);
    } catch (error: any) {
      console.error('Failed to load model preferences:', error);
      showNotification('error', 'Failed to load model preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleModelChange = async (componentName: string, field: 'primary' | 'backup', modelId: string) => {
    const component = components.find(c => c.name === componentName);
    if (!component) return;

    setSaving(componentName);

    try {
      const payload = {
        component: componentName,
        primary: field === 'primary' ? modelId : component.primary,
        backup: field === 'backup' ? modelId : component.backup
      };

      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/admin/model-preferences', {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `${response.status} ${response.statusText}`);
      }

      // Update local state
      setComponents(prev => prev.map(c =>
        c.name === componentName
          ? { ...c, [field]: modelId }
          : c
      ));

      showNotification('success', `Updated ${component.displayName} ${field} model`);
    } catch (error: any) {
      console.error('Failed to update model:', error);
      const errorMsg = error?.message || 'Unknown error';
      showNotification('error', `Failed to update model: ${errorMsg}`);
    } finally {
      setSaving(null);
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const getModelStats = (modelId: string): ModelStats => {
    const model = models.find(m => m.id === modelId);
    return {
      promptCost: model && model.pricing && model.pricing.prompt ? parseFloat(model.pricing.prompt) * 1000000 : 0,
      completionCost: model && model.pricing && model.pricing.completion ? parseFloat(model.pricing.completion) * 1000000 : 0,
      contextLength: model && model.context_length ? model.context_length : 0
    };
  };

  const getFilteredModels = () => {
    return models.filter(model => {
      const matchesSearch = !searchTerm ||
        model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        model.id.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesProvider = filterProvider === 'all' ||
        model.id.toLowerCase().includes(filterProvider.toLowerCase());

      return matchesSearch && matchesProvider;
    });
  };

  const getProviders = () => {
    const providers = new Set<string>();
    models.forEach(model => {
      const provider = model.id.split('/')[0];
      if (provider) providers.add(provider);
    });
    return Array.from(providers).sort();
  };

  const ModelSelect = ({
    value,
    onChange,
    label,
    componentName
  }: {
    value: string;
    onChange: (modelId: string) => void;
    label: string;
    componentName: string;
  }) => {
    const filteredModels = getFilteredModels();
    const currentModel = models.find(m => m.id === value);
    const stats = getModelStats(value);
    const isSaving = saving === componentName;

    return (
      <div className="model-select">
        <label className="model-select-label">{label}</label>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={isSaving}
          className="model-select-input"
        >
          <option value="">Select a model...</option>
          {filteredModels.map(model => (
            <option key={model.id} value={model.id}>
              {model.name} ({model.id})
            </option>
          ))}
        </select>
        {currentModel && (
          <div className="model-stats">
            <span className="model-stat">
              💰 ${stats.promptCost.toFixed(3)}/M prompt
            </span>
            <span className="model-stat">
              💰 ${stats.completionCost.toFixed(3)}/M completion
            </span>
            <span className="model-stat">
              📏 {stats.contextLength.toLocaleString()} tokens
            </span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="model-preferences-loading">
        <div className="spinner"></div>
        <p>Loading model preferences...</p>
      </div>
    );
  }

  return (
    <div className="model-preferences-container">
      <div className="model-preferences-header">
        <h2>🤖 AI Model Preferences</h2>
        <p className="subtitle">
          Configure which LLM models to use for each system component.
          Primary model is used first, backup model is used if primary fails.
        </p>
      </div>

      {notification && (
        <div className={`notification notification-${notification.type}`}>
          {notification.type === 'success' ? '✅' : '❌'} {notification.message}
        </div>
      )}

      <div className="model-filters">
        <div className="filter-group">
          <label>🔍 Search Models:</label>
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <label>🏢 Provider:</label>
          <select
            value={filterProvider}
            onChange={(e) => setFilterProvider(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Providers ({models.length})</option>
            {getProviders().map(provider => (
              <option key={provider} value={provider}>
                {provider} ({models.filter(m => m.id.startsWith(provider + '/')).length})
              </option>
            ))}
          </select>
        </div>
        <div className="filter-stats">
          <span>📊 Total Models: {models.length}</span>
          <span>🔎 Filtered: {getFilteredModels().length}</span>
        </div>
      </div>

      <div className="components-grid">
        {components.map(component => {
          const isSaving = saving === component.name;

          return (
            <div key={component.name} className={`component-card ${isSaving ? 'saving' : ''}`}>
              <div className="component-header">
                <h3>{component.displayName}</h3>
                <p className="component-description">{component.description}</p>
              </div>

              <div className="component-models">
                <ModelSelect
                  value={component.primary}
                  onChange={(modelId) => handleModelChange(component.name, 'primary', modelId)}
                  label="Primary Model"
                  componentName={component.name}
                />

                <ModelSelect
                  value={component.backup}
                  onChange={(modelId) => handleModelChange(component.name, 'backup', modelId)}
                  label="Backup Model (Fallback)"
                  componentName={component.name}
                />
              </div>

              {isSaving && (
                <div className="saving-indicator">
                  <div className="spinner-small"></div>
                  <span>Saving...</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        .model-preferences-container {
          padding: 24px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .model-preferences-header {
          margin-bottom: 32px;
        }

        .model-preferences-header h2 {
          font-size: 28px;
          font-weight: 600;
          margin-bottom: 8px;
          color: #1a1a1a;
        }

        .subtitle {
          color: #666;
          font-size: 14px;
          line-height: 1.6;
        }

        .notification {
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 24px;
          font-weight: 500;
        }

        .notification-success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .notification-error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .model-filters {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 12px;
          margin-bottom: 24px;
          display: flex;
          gap: 16px;
          align-items: flex-end;
          flex-wrap: wrap;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
          flex: 1;
          min-width: 200px;
        }

        .filter-group label {
          font-size: 13px;
          font-weight: 500;
          color: #555;
        }

        .filter-input, .filter-select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          background: white;
        }

        .filter-input:focus, .filter-select:focus {
          outline: none;
          border-color: #4CAF50;
          box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
        }

        .filter-stats {
          display: flex;
          gap: 16px;
          font-size: 13px;
          color: #666;
          padding: 8px 0;
        }

        .components-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
          gap: 24px;
        }

        .component-card {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 12px;
          padding: 20px;
          transition: all 0.2s;
        }

        .component-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .component-card.saving {
          opacity: 0.7;
          pointer-events: none;
        }

        .component-header {
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid #f0f0f0;
        }

        .component-header h3 {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 4px;
          color: #2c3e50;
        }

        .component-description {
          font-size: 13px;
          color: #666;
          line-height: 1.5;
        }

        .component-models {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .model-select {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .model-select-label {
          font-size: 13px;
          font-weight: 600;
          color: #555;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .model-select-input {
          padding: 10px 12px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          transition: border-color 0.2s;
        }

        .model-select-input:focus {
          outline: none;
          border-color: #4CAF50;
        }

        .model-select-input:disabled {
          background: #f5f5f5;
          cursor: not-allowed;
        }

        .model-stats {
          display: flex;
          gap: 12px;
          padding: 8px 12px;
          background: #f8f9fa;
          border-radius: 6px;
          font-size: 12px;
          flex-wrap: wrap;
        }

        .model-stat {
          color: #666;
          white-space: nowrap;
        }

        .saving-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 12px;
          padding: 8px 12px;
          background: #e3f2fd;
          border-radius: 6px;
          font-size: 13px;
          color: #1976d2;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #4CAF50;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid #e3f2fd;
          border-top: 2px solid #1976d2;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .model-preferences-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          gap: 16px;
        }

        .model-preferences-loading p {
          color: #666;
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .components-grid {
            grid-template-columns: 1fr;
          }

          .model-filters {
            flex-direction: column;
            align-items: stretch;
          }

          .filter-group {
            min-width: auto;
          }
        }
      `}</style>
    </div>
  );
}
